import { useQuery } from '@tanstack/react-query';
import DropdownStyled from '../../../components/inputs/StyledInputs/DropdownStyled';
import { useCompanyContext } from '../../../context/CompanyContext';
import { fetchUserCompanies } from '../../../services/admin/UsersService';
import { DropdownChangeParams } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { useCookies } from 'react-cookie';
import { Company } from '../../../Interfaces/admin/User.interfaces';
import { useSearchParams } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { getCompanyLabel } from '@/src/utils/formatUtils';
import { useCallback, useEffect, useState } from 'react';

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const CompanySelector = () => {
  const [cookies, setCookie] = useCookies(['company']);
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompany, setSelectedCompany } = useCompanyContext();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    selectedCompany && !selectedCompany.isActive ? ['inactive'] : ['active']
  );
  const [showStatusToggle, setShowStatusToggle] = useState(false);

  const changeCompanyCookie = (companyID: string) => {
    const expirationTime = new Date(Date.now() + 12 * 60 * 60 * 1000);
    setCookie('company', companyID, { expires: expirationTime, path: '/' });
  };
  const changeCompanyCookieCB = useCallback(changeCompanyCookie, [setCookie]);

  const decryptCompany = (): string | null => {
    const companyParam = searchParams.get('company');
    const secretKey = process.env['NX_CRYPTO_PUBLIC_KEY'];

    let company = null;
    if (companyParam && secretKey) {
      try {
        const bytes = CryptoJS.AES.decrypt(companyParam, secretKey);
        company = bytes.toString(CryptoJS.enc.Utf8);
      } catch (error) {
        console.error('Error decrypting company:', error);
      }
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('company');

    setSearchParams(newSearchParams);
    return company;
  };
  const decryptCompanyCB = useCallback(decryptCompany, [
    searchParams,
    setSearchParams,
  ]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['getUserCompanies'],
    queryFn: async () => {
      return fetchUserCompanies();
    },
    onSuccess: (data) => {
      setShowStatusToggle(
        data.some((company: Company) => !company.isActive) &&
          data.some((company: Company) => company.isActive !== false)
      );
      data.sort((a: Company, b: Company) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });

      const companyID = decryptCompany();
      const companyData = data.find(
        (companyData: Company) =>
          companyData.id === companyID || companyData.code === companyID
      );

      if (companyData) {
        changeCompanyCookie(companyData.id);
        setSelectedCompany(companyData);
        if (!companyData.isActive) {
          setSelectedStatuses(['inactive']);
        }
      } else {
        const cookieCompany = data.find(
          (company: Company) => company.id === cookies.company
        );
        const company = cookieCompany || data[0];
        if (company && !company.isActive) {
          setSelectedStatuses(['inactive']);
        } else if (company && company.isActive) {
          setSelectedStatuses(['active']);
        }
        setSelectedCompany(company);
      }
    },
    refetchOnWindowFocus: false,
  });

  const allCompanies: Company[] = data ?? [];

  const showActive = selectedStatuses.includes('active');
  const showInactive = selectedStatuses.includes('inactive');

  const filteredCompanies = allCompanies.filter((company: Company) => {
    if (showActive && showInactive) return true;
    if (showActive) return company.isActive !== false;
    return company.isActive === false;
  });

  const handleSelecCompany = (id: string | null) => {
    const companyData = data.find((company: Company) => company.id === id);
    setSelectedCompany(companyData);
    changeCompanyCookie(companyData.id);
  };

  const handleStatusChange = (value: string[]) => {
    if (!value || value.length === 0) return;
    setSelectedStatuses(value);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterTemplate = (options: any) => (
    <div>
      {showStatusToggle && (
        <div className="flex justify-content-center p-2 pb-1">
          <SelectButton
            className="tabSelectButton text-12px"
            value={selectedStatuses}
            options={statusOptions}
            optionLabel="label"
            optionValue="value"
            multiple
            onChange={(e) => handleStatusChange(e.value)}
          />
        </div>
      )}
      {options.element}
    </div>
  );

  useEffect(() => {
    if (!data || !searchParams.get('company')) return;

    const companyID = decryptCompanyCB();
    const companyData = data.find(
      (c: Company) => c.id === companyID || c.code === companyID
    );

    if (companyData) {
      changeCompanyCookieCB(companyData.id);
      setSelectedCompany(companyData);
      setSelectedStatuses(
        companyData.isActive === false ? ['inactive'] : ['active']
      );
    }
  }, [
    searchParams,
    changeCompanyCookieCB,
    data,
    decryptCompanyCB,
    setSelectedCompany,
  ]);

  return (
    <DropdownStyled
      isTitle={true}
      filter={true}
      resetFilterOnHide={true}
      isLoading={isLoading}
      isDisabled={isError}
      options={filteredCompanies.map((company: Company) => ({
        ...company,
        label: getCompanyLabel(company),
      }))}
      clearable={false}
      error={isError}
      placeholder="Select company"
      valueField="id"
      labelField="label"
      filterTemplate={filterTemplate}
      {...{
        value: selectedCompany ? selectedCompany.id : undefined,
        onChange: (e: DropdownChangeParams) => {
          handleSelecCompany(e.target.value);
        },
      }}
      onHide={() => {
        const option = selectedCompany?.isActive ? 'active' : 'inactive';
        if (!selectedStatuses.includes(option)) {
          setSelectedStatuses((status) => [...status, option]);
        }
      }}
    />
  );
};

export default CompanySelector;
